import React from 'react';
import { ColumnsType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { Tag, Tooltip } from 'antd';

import { SortDirection } from 'api/graphql-global-types';
import {
  InterviewChatOrderBy,
  InterviewChatRow,
} from 'api/interviewChats/types';

import {
  STATE_COLORS,
  formatDateTime,
  formatRelative,
} from 'helpers/interviewChats';

import Person from './components/Person/Person';
import styles from './InterviewChats.module.scss';

/**
 * Column key → sort field. Columns absent here are not sortable: GetStream sorts channels
 * on its own indexed fields only, and message count is not one of them.
 */
export const SORT_KEY_TO_ORDER_BY: Record<string, InterviewChatOrderBy> = {
  lastMessageAt: 'LastMessageAt',
  createdAt: 'CreatedAt',
};

/**
 * GetStream sorts and pages the list, so the columns are controlled: only the active
 * `orderBy` column shows an arrow.
 */
export const buildChannelColumns = (
  orderBy: InterviewChatOrderBy,
  direction: SortDirection
): ColumnsType<InterviewChatRow> => {
  const sortOrderFor = (key: string): SortOrder | null => {
    if (SORT_KEY_TO_ORDER_BY[key] !== orderBy) return null;
    return direction === SortDirection.ASC ? 'ascend' : 'descend';
  };

  // Descending first: the interesting chats are the busiest and the most recent.
  const sortDirections: SortOrder[] = ['descend', 'ascend'];

  return [
    {
      title: 'Host',
      key: 'host',
      render: (_value, row) => (
        <Person person={row.host} fallbackLabel="No host" />
      ),
    },
    {
      title: 'Guest',
      key: 'guest',
      render: (_value, row) => (
        <Person person={row.guest} fallbackLabel="No guest" />
      ),
    },
    {
      title: 'State',
      key: 'state',
      width: 100,
      render: (_value, row) => (
        <Tag color={STATE_COLORS[row.state]}>{row.state}</Tag>
      ),
    },
    {
      title: 'Messages',
      key: 'messageCount',
      width: 100,
      align: 'right',
      render: (_value, row) => (
        <div className={styles.numericCell}>{row.messageCount}</div>
      ),
    },
    {
      title: 'Last activity',
      key: 'lastMessageAt',
      width: 150,
      sorter: true,
      sortDirections,
      sortOrder: sortOrderFor('lastMessageAt'),
      render: (_value, row) => (
        <Tooltip title={formatDateTime(row.lastMessageAt)}>
          <span>{formatRelative(row.lastMessageAt)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Created',
      key: 'createdAt',
      width: 150,
      sorter: true,
      sortDirections,
      sortOrder: sortOrderFor('createdAt'),
      render: (_value, row) => (
        <Tooltip title={formatDateTime(row.createdAt)}>
          <span>{formatRelative(row.createdAt)}</span>
        </Tooltip>
      ),
    },
  ];
};
